import { useCallback, useEffect, useState } from 'react';
import { initials } from '../components/TaskCard';
import { api } from '../lib/api';
import type { Activity as ActivityData } from '../lib/types';
import { useStore } from '../store/useStore';

const RANGES = [7, 30, 90] as const;

/**
 * Who finished what, and when. Read from the completion log rather than from the
 * board, so reopening a task does not quietly rewrite the record.
 *
 * One series throughout — "completions" — so every mark wears the same accent and
 * the charts need no legend. People are compared in a table rather than as eight
 * coloured series, which is both more readable and the only honest option past a
 * handful of names.
 */
export function Activity() {
  const groupId = useStore((s) => s.activeGroupId);
  const fields = useStore((s) => s.fields);

  const [days, setDays] = useState<number>(30);
  const [data, setData] = useState<ActivityData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      setData(await api<ActivityData>(`/api/groups/${groupId}/activity?days=${days}`));
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not load activity');
    } finally {
      setLoading(false);
    }
  }, [groupId, days]);

  useEffect(() => {
    void load();
  }, [load]);

  const totalCompleted = data?.people.reduce((sum, person) => sum + person.completed, 0) ?? 0;
  const busiest = data?.daily.reduce(
    (best, day) => (day.completed > best.completed ? day : best),
    { date: '', completed: 0 },
  );
  const activeDays = data?.daily.filter((day) => day.completed > 0).length ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-base font-semibold tracking-tight">Activity</h1>
          <p className="mt-0.5 text-sm text-ink-faint">
            Every completed task, counted on the day it was closed.
          </p>
        </div>

        {/* Filters sit in one row above the charts. */}
        <div className="flex items-center gap-1 rounded-full border border-line p-0.5">
          {RANGES.map((range) => (
            <button
              key={range}
              type="button"
              onClick={() => setDays(range)}
              aria-pressed={days === range}
              className={`rounded-full px-3 py-1 text-xs transition-colors ${
                days === range
                  ? 'bg-elevated font-medium text-ink'
                  : 'text-ink-soft hover:text-ink'
              }`}
            >
              {range} days
            </button>
          ))}
        </div>
      </div>

      {error && (
        <p className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
          {error}
        </p>
      )}

      {!data && loading && <p className="text-sm text-ink-faint">Counting…</p>}

      {data && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <HeroTile label={`Completed in ${days} days`} value={totalCompleted} />
            <StatTile
              label="Days with activity"
              value={`${activeDays}`}
              note={`of ${data.daily.length}`}
            />
            <StatTile
              label="Busiest day"
              value={busiest && busiest.completed > 0 ? `${busiest.completed}` : '—'}
              note={busiest && busiest.completed > 0 ? formatDay(busiest.date) : 'nothing yet'}
            />
            <StatTile
              label="People contributing"
              value={`${data.people.length}`}
              note={data.people[0] ? `most: ${data.people[0].displayName}` : 'nobody yet'}
            />
          </div>

          <DailyChart daily={data.daily} />

          <PeopleTable people={data.people} total={totalCompleted} />

          {data.fields.length > 0 && (
            <section className="card p-4">
              <h2 className="text-sm font-semibold">By dev field</h2>
              <ul className="mt-3 flex flex-col gap-2">
                {data.fields.map((row) => {
                  const field = row.fieldId
                    ? fields.find((f) => f.id === row.fieldId)
                    : undefined;
                  const share = totalCompleted > 0 ? row.completed / totalCompleted : 0;
                  return (
                    <li key={row.fieldId ?? 'none'} className="flex items-center gap-3">
                      <span
                        aria-hidden="true"
                        className="size-1.5 shrink-0 rounded-full"
                        style={{ backgroundColor: field?.color ?? 'var(--color-ink-faint)' }}
                      />
                      <span className="w-32 shrink-0 truncate text-xs text-ink-soft">
                        {field?.name ?? 'Unsorted'}
                      </span>
                      <span className="h-1.5 min-w-0 flex-1 rounded-full bg-elevated">
                        <span
                          className="block h-full rounded-full bg-accent"
                          style={{ width: `${Math.max(share * 100, 2)}%` }}
                        />
                      </span>
                      <span className="w-8 shrink-0 text-right text-xs text-ink tabular-nums">
                        {row.completed}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          <RecentLog recent={data.recent} />
        </>
      )}
    </div>
  );
}

/**
 * Completions per day. One series, so the title carries the identity and no legend
 * is needed; only the tallest column is labelled directly, and the rest are read
 * off the axis or the tooltip.
 */
function DailyChart({ daily }: { daily: ActivityData['daily'] }) {
  const [hovered, setHovered] = useState<number | null>(null);

  const peak = Math.max(1, ...daily.map((day) => day.completed));
  const peakIndex = daily.findIndex((day) => day.completed === peak);
  const labelEvery = Math.max(1, Math.ceil(daily.length / 7));

  return (
    <section className="card p-4">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold">Tasks completed per day</h2>
        <span className="text-[11px] text-ink-faint">
          {formatDay(daily[0]?.date ?? '')} – {formatDay(daily.at(-1)?.date ?? '')}
        </span>
      </div>

      <div className="relative mt-5">
        {/* Hairline gridline at the peak, carrying the only y value worth printing. */}
        <div className="absolute inset-x-0 top-0 flex items-center gap-2">
          <span className="text-[10px] text-ink-faint tabular-nums">{peak}</span>
          <span className="h-px min-w-0 flex-1 bg-line" />
        </div>

        <div className="flex h-36 items-end gap-0.5 pt-4">
          {daily.map((day, index) => {
            const height = (day.completed / peak) * 100;
            return (
              <div
                key={day.date}
                onMouseEnter={() => setHovered(index)}
                onMouseLeave={() => setHovered(null)}
                className="relative flex h-full min-w-0 flex-1 cursor-default flex-col justify-end"
              >
                {index === peakIndex && day.completed > 0 && (
                  <span className="mb-1 text-center text-[10px] text-ink-soft tabular-nums">
                    {day.completed}
                  </span>
                )}

                <span
                  className={`mx-auto block w-full max-w-6 rounded-t transition-colors ${
                    day.completed > 0
                      ? hovered === index
                        ? 'bg-[#6ba1ff]'
                        : 'bg-accent'
                      : 'bg-line'
                  }`}
                  style={{ height: day.completed > 0 ? `${Math.max(height, 3)}%` : '2px' }}
                />

                {hovered === index && (
                  <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1.5 -translate-x-1/2 rounded-md border border-line-strong bg-elevated px-2 py-1 text-center shadow-lg shadow-black/40">
                    <p className="text-[10px] whitespace-nowrap text-ink-faint">
                      {formatDay(day.date)}
                    </p>
                    <p className="text-xs font-semibold whitespace-nowrap text-ink">
                      {day.completed} {day.completed === 1 ? 'task' : 'tasks'}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-1.5 flex gap-0.5 border-t border-line pt-1.5">
          {daily.map((day, index) => (
            <span
              key={day.date}
              className="min-w-0 flex-1 text-center text-[9px] text-ink-faint"
            >
              {index % labelEvery === 0 ? formatDay(day.date) : ''}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * A table, not eight coloured series: past a handful of names colour stops telling
 * people apart, and the numbers are what gets compared anyway. The bar is a
 * magnitude cue on one hue, read alongside the value next to it.
 */
function PeopleTable({
  people,
  total,
}: {
  people: ActivityData['people'];
  total: number;
}) {
  const top = Math.max(1, ...people.map((person) => person.completed));

  return (
    <section className="card overflow-hidden">
      <div className="flex items-baseline justify-between gap-3 px-4 pt-4">
        <h2 className="text-sm font-semibold">Who finished what</h2>
        <span className="text-[11px] text-ink-faint tabular-nums">{total} in total</span>
      </div>

      {people.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-ink-faint">
          Nothing has been completed in this window yet.
        </p>
      ) : (
        <table className="mt-3 w-full text-sm">
          <thead>
            <tr className="border-y border-line text-left text-[11px] tracking-wide text-ink-faint uppercase">
              <th scope="col" className="px-4 py-2 font-medium">
                Person
              </th>
              <th scope="col" className="py-2 font-medium">
                Completed
              </th>
              <th scope="col" className="hidden py-2 text-right font-medium sm:table-cell">
                Active days
              </th>
              <th scope="col" className="hidden py-2 text-right font-medium sm:table-cell">
                Reopened
              </th>
              <th scope="col" className="px-4 py-2 text-right font-medium">
                Last
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {people.map((person) => (
              <tr key={person.userId ?? person.displayName}>
                <td className="px-4 py-2.5">
                  <span className="flex items-center gap-2">
                    <span className="grid size-6 shrink-0 place-items-center rounded-full bg-elevated text-[9px] font-semibold text-ink-soft ring-1 ring-line">
                      {initials(person.displayName)}
                    </span>
                    <span className="truncate">{person.displayName}</span>
                  </span>
                </td>
                <td className="py-2.5">
                  <span className="flex items-center gap-2">
                    <span className="h-1.5 w-16 shrink-0 rounded-full bg-elevated sm:w-24">
                      <span
                        className="block h-full rounded-full bg-accent"
                        style={{ width: `${Math.max((person.completed / top) * 100, 3)}%` }}
                      />
                    </span>
                    <span className="tabular-nums">{person.completed}</span>
                  </span>
                </td>
                <td className="hidden py-2.5 text-right text-ink-soft tabular-nums sm:table-cell">
                  {person.activeDays}
                </td>
                <td className="hidden py-2.5 text-right text-ink-faint tabular-nums sm:table-cell">
                  {person.reopened || '—'}
                </td>
                <td className="px-4 py-2.5 text-right text-xs text-ink-faint">
                  {person.lastAt ? formatDay(person.lastAt) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/** The raw record behind the counts, newest first. */
function RecentLog({ recent }: { recent: ActivityData['recent'] }) {
  const members = useStore((s) => s.members);
  if (recent.length === 0) return null;

  return (
    <section className="card p-4">
      <h2 className="text-sm font-semibold">Latest</h2>
      <ul className="mt-3 flex flex-col divide-y divide-line">
        {recent.map((event) => {
          const who = event.userId ? members.find((m) => m.id === event.userId) : undefined;
          const reopened = event.kind === 'reopened';
          return (
            <li key={event.id} className="flex items-center gap-3 py-2">
              {/* Status colour, always with its own icon and word. */}
              <span
                className={`flex shrink-0 items-center gap-1 text-[11px] font-medium ${
                  reopened ? 'text-blocked' : 'text-done'
                }`}
              >
                {reopened ? (
                  <svg viewBox="0 0 16 16" className="size-3.5" fill="currentColor" aria-hidden="true">
                    <path d="M8 3a5 5 0 1 0 4.9 6 .75.75 0 1 1 1.47.3A6.5 6.5 0 1 1 8 1.5c1.8 0 3.42.73 4.6 1.9V2.25a.75.75 0 0 1 1.5 0V5.5a.75.75 0 0 1-.75.75h-3.25a.75.75 0 0 1 0-1.5h1.42A5 5 0 0 0 8 3Z" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 16 16" className="size-3.5" fill="currentColor" aria-hidden="true">
                    <path d="M13.5 4.2a.9.9 0 0 1 0 1.28l-6.1 6.1a.9.9 0 0 1-1.28 0L2.9 8.36a.9.9 0 0 1 1.28-1.27l2.58 2.58 5.46-5.47a.9.9 0 0 1 1.28 0Z" />
                  </svg>
                )}
                {reopened ? 'Reopened' : 'Done'}
              </span>

              <span className="min-w-0 flex-1 truncate text-sm">{event.title}</span>

              <span className="shrink-0 text-xs text-ink-soft">
                {who?.displayName ?? 'Unknown'}
              </span>
              <span className="w-24 shrink-0 text-right text-[11px] text-ink-faint">
                {formatMoment(event.occurredAt)}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** The one number the page leads with. */
function HeroTile({ label, value }: { label: string; value: number }) {
  return (
    <div className="card flex flex-col gap-1 p-4">
      <span className="text-xs text-ink-faint">{label}</span>
      <span className="text-5xl leading-none font-semibold tracking-tight">{value}</span>
    </div>
  );
}

function StatTile({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="card flex flex-col gap-1 p-4">
      <span className="text-xs text-ink-faint">{label}</span>
      <span className="text-2xl leading-none font-semibold tracking-tight">{value}</span>
      <span className="text-[11px] text-ink-faint">{note}</span>
    </div>
  );
}

function formatDay(value: string): string {
  if (!value) return '';
  return new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function formatMoment(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}
